import React, {useEffect, useMemo, useState} from 'react';
import {MaterialFormFieldOptions} from 'shared/types/materials';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {hasDoorSet} from 'components/customer/Materials/store/materialSlice';
import styled, {css} from 'styled-components';
import {useProductContext} from 'contexts';
import {getValidStructure} from 'hooks/QFP/Shared';
import {touchDevices, nonTouchDevices} from 'shared/Breakpoints';
import {DoorStyle} from 'components/customer/Product/Specs/Materials/DoorStyle';
import {Material} from 'components/customer/Product/Specs/Materials/Material';
import {
    MaterialSearch,
    ResponsiveProps,
} from 'components/customer/Product/Specs/Materials/MaterialSearch';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {selectProductType} from 'components/customer/Product/store/productSlice';
import {checkHasDoor} from 'shared/helpers/manualHasDoor';

interface MaterialSimplifiedType {
    isVisible?: boolean;
    isExterior?: boolean;
    thumb: JSX.Element;
    field: MaterialFormFieldOptions;
}

export const MaterialSimplified = ({
    isExterior = true,
    isVisible = true,
    thumb,
    field,
}: MaterialSimplifiedType): JSX.Element => {
    const dispatch = useAppDispatch();
    const {isMiniBrowser, formStructure} = useProductContext();

    const productType = useAppSelector(selectProductType);

    const [stack, setStack] = useState(false);

    const productInfo = useMemo(() => {
        if (formStructure) {
            const hasDoors = checkHasDoor(productType);

            if (hasDoors) {
                return {
                    ...getValidStructure(formStructure),
                    hasDoors,
                };
            } else {
                return getValidStructure(formStructure);
            }
        }
    }, [formStructure, productType]);

    const showBlueLine = useMemo(() => {
        let show = false;

        if (formStructure) {
            for (const section of formStructure) {
                if (section.name == 'Materials') {
                    for (const fieldset of section.fieldsets) {
                        if (fieldset.name == 'materials') {
                            const fieldNames = fieldset.fields.map(
                                (field) => field.name
                            );

                            show = fieldNames.includes(
                                'include_drawer_faces_ext'
                            );
                        }
                    }
                }
            }
        }

        return show;
    }, [formStructure]);

    useEffect(() => {
        if (isExterior) {
            dispatch(hasDoorSet(productInfo?.hasDoors));
        }
    }, [productInfo]);

    const container = useResizeObserver((entries) => {
        if (entries.length > 0) {
            const {contentRect} = entries[0];
            const {width} = contentRect;

            setStack(width < 586);
        }
    });

    return (
        <section
            style={{
                marginBottom: '15px',
            }}
            className={!isVisible ? 'hidden_field' : ''}>
            {showBlueLine && isExterior ? <BlueLine /> : null}
            <MaterialsContainer
                $isExterior={isExterior}
                $stack={stack}
                $isMiniBrowser={isMiniBrowser}
                ref={container}>
                {isExterior &&
                (productInfo?.hasDoors || productInfo?.hasDrawers) ? (
                    <DoorStyle
                        isExterior={isExterior}
                        isMiniBrowser={isMiniBrowser}
                        productInfo={productInfo}
                    />
                ) : null}
                <Material
                    isMiniBrowser={isMiniBrowser}
                    isExterior={isExterior}
                    thumb={thumb}
                    productInfo={productInfo}
                />

                <MaterialSearch
                    isExterior={isExterior}
                    field={field}
                    isMiniBrowser={isMiniBrowser}
                    productInfo={productInfo}
                />
            </MaterialsContainer>
        </section>
    );
};

const BlueLine = styled.div`
    border-top: 1px solid ${({theme}) => theme?.colors?.secondary?.main};
    margin: -15px 5px 0;
`;

const MobileMaterialsContainer = css`
    flex-wrap: wrap;
    justify-content: space-between;
`;

export const MaterialsContainer = styled.div<ResponsiveProps>`
    display: flex;
    gap: 15px;
    flex-direction: row;

    ${({$isExterior = false}) => {
        if ($isExterior) {
            return `
                margin-top: 15px;
            `;
        }
    }}

    ${({$isMiniBrowser}) => $isMiniBrowser && MobileMaterialsContainer}

    ${touchDevices`
        ${MobileMaterialsContainer}
    `}

    ${nonTouchDevices<ResponsiveProps>`
        ${({$stack, $isMiniBrowser}) => {
            if ($stack && !$isMiniBrowser) {
                return `
                    flex-wrap: wrap;
                    justify-content: center;
                `;
            }
        }}
    `}
`;
